import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type JSX } from 'react';
import { generatePath } from 'react-router';

import { TprmAssessmentApi } from 'Api/TPRM/TprmAssessmentApi';
import { UcrmAssessmentApi } from 'Api/UCRM/UcrmAssessmentApi';
import { AdvancedFeatureUnavailableExplanation } from 'Components/AdvancedFeatureUnavailableExplanation/AdvancedFeatureUnavailableExplanation';
import { Button, Link } from 'Components/Buttons/Buttons';
import { OverflowMenu } from 'Components/Buttons/OverflowMenu';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { Text } from 'Components/Text/Text';
import { FormFieldTooltip } from 'Components/Tooltips/FormFieldTooltip';
import { ICON_ARROW_RIGHT, ICON_AUTO_ADVANCE, ICON_DELETE_REMOVE, ICON_DISABLED_FEATURE, ICON_RESTART } from 'Config/Icons';
import { TPRM_FINAL_REVIEW, TPRM_INHERENT_RISK_QUESTIONNAIRE, UCRM_FINAL_REVIEW, UCRM_INHERENT_RISK_QUESTIONNAIRE } from 'Config/PathTemplates';
import { iso8601ToJsDate } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { AssessmentSubjectBase } from 'Models/AssessmentSubject';
import { AdvancedFeature, getDescriptionForAdvancedFeature } from 'Models/ClientDetailsModel';
import { AssessmentWorkflowState } from 'Models/DueDiligenceWorkflow';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { AssessmentsDisplayInfo } from 'Models/UcrmTprm';

import styles from './DueDiligenceWorkflow.module.css';

type WorkflowStatus = 'complete' | 'inProgress' | 'overdue' | 'idle';

interface Statuses {
    irqStatus: WorkflowStatus;
    irqStatusText: string;

    questionnairesStatus: WorkflowStatus;
    questionnairesStatusText: string;

    assessmentsStatus: WorkflowStatus;
    assessmentsStatusText: string;

    finalReviewStatus: WorkflowStatus;
    finalReviewStatusText: string;
}

const getBorderClass = (status: WorkflowStatus): string => {
    switch (status) {
        case 'complete':
            return styles.stationCompleted;
        case 'inProgress':
            return styles.stationInProgress;
        case 'overdue':
            return styles.stationOverdue;
        case 'idle':
            return styles.stationPending;
    }
};

const getStatusBadgeClass = (status: WorkflowStatus): string => {
    switch (status) {
        case 'complete':
            return styles.statusCompleted;
        case 'inProgress':
            return styles.statusInProgress;
        case 'overdue':
            return styles.statusOverdue;
        case 'idle':
            return styles.statusPending;
    }
};

interface DueDiligenceWorkflowProps {
    assessmentApi: TprmAssessmentApi | UcrmAssessmentApi;
    assessmentSubject: AssessmentSubjectBase;
    assessmentsDisplayInfo: AssessmentsDisplayInfo;
    module: DueDiligenceModule;
    onSelectClearWorkflow: () => void;
    onSelectRestartWorkflow: () => void;

    /** UCRM only. Questionnaires are answered by SummIT users (respondents) rather than third-party contacts. When omitted, the link to invite respondents is not displayed. */
    onSelectInviteRespondents?: () => void;
    /** TPRM only. Omitted for UCRM, since use cases do not have contacts to invite. When omitted, the link to invite contacts is not displayed. */
    onSelectInviteServiceContacts?: () => void;
}

/**
 * Displays the series of "stations" that questionnaires and assessments move through as a due diligence workflow is completed for an assessment subject. Used on the TPRM Service Dashboard and the UCRM Use Case Dashboard.
 * If the assessment subject is using shared due diligence assessments provided by another assessment subject, the questionnaires station and assessments station simply indicate that assessments are being shared, rather than showing further details. This makes it obvious to the user that the source-of-truth for progress lives elsewhere, and keeps us from having to worry about retrieving and parsing parent information.
 */
export const DueDiligenceWorkflow = (props: DueDiligenceWorkflowProps): JSX.Element => {
    const now = new Date();

    const cachedData = useCachedData();
    const autoAdvanceFeature = props.module === ProductModule.UCRM ? AdvancedFeature.AI_UCRM_AUTO_ADVANCE : AdvancedFeature.AI_TPRM_AUTO_ADVANCE;
    const autoAdvanceAvailability = cachedData.checkAdvancedFeatureAvailability(autoAdvanceFeature);

    const statuses: Statuses = (() => {
        let irqStatus: WorkflowStatus;
        let irqStatusText: string;
        if (props.assessmentSubject.assessment_workflow_data.irq_completed_time) {
            irqStatus = 'complete';
            irqStatusText = 'Complete';
        } else if (props.assessmentSubject.assessment_workflow_setup.irq_target_completion_date !== undefined && now > iso8601ToJsDate(props.assessmentSubject.assessment_workflow_setup.irq_target_completion_date)) {
            irqStatus = 'overdue';
            irqStatusText = 'Overdue';
        } else {
            irqStatus = 'inProgress';
            irqStatusText = 'In progress';
        }

        const numberOfQuestionnairesComplete = props.assessmentsDisplayInfo.usingCommonAssessments ? [] : props.assessmentsDisplayInfo.assessments.filter((assessment) => assessment.questionnaire_submitted_timestamp).length;
        const numberOfAssessmentsComplete = props.assessmentsDisplayInfo.usingCommonAssessments ? [] : props.assessmentsDisplayInfo.assessments.filter((assessment) => assessment.assessment_submitted_timestamp).length;

        const questionnairesStatusText = props.assessmentsDisplayInfo.usingCommonAssessments ? 'Using shared due diligence assessments' : `${numberOfQuestionnairesComplete} submitted`;
        let questionnairesStatus: WorkflowStatus;
        if (props.assessmentsDisplayInfo.usingCommonAssessments) {
            questionnairesStatus = 'idle';
        } else if (irqStatus !== 'complete') {
            questionnairesStatus = 'idle';
        } else if (numberOfQuestionnairesComplete === props.assessmentsDisplayInfo.assessments.length) {
            questionnairesStatus = 'complete';
        } else {
            const anyQuestionnairesOverdue = props.assessmentsDisplayInfo.assessments.some((assessment) => !assessment.questionnaire_submitted_timestamp && assessment.questionnaire_due_date && now > iso8601ToJsDate(assessment.questionnaire_due_date));
            questionnairesStatus = anyQuestionnairesOverdue ? 'overdue' : 'inProgress';
        }

        const assessmentsStatusText = props.assessmentsDisplayInfo.usingCommonAssessments ? 'Using shared due diligence assessments' : `${numberOfAssessmentsComplete} reviewed`;
        let assessmentsStatus: WorkflowStatus;
        if (props.assessmentsDisplayInfo.usingCommonAssessments) {
            assessmentsStatus = 'idle';
        } else if (irqStatus !== 'complete') {
            assessmentsStatus = 'idle';
        } else if (numberOfAssessmentsComplete === props.assessmentsDisplayInfo.assessments.length) {
            assessmentsStatus = 'complete';
        } else {
            const anyAssessmentsOverdue = props.assessmentsDisplayInfo.assessments.some((assessment) => !assessment.assessment_submitted_timestamp && assessment.assessment_due_date && now > iso8601ToJsDate(assessment.assessment_due_date));
            assessmentsStatus = anyAssessmentsOverdue ? 'overdue' : 'inProgress';
        }

        let finalReviewStatus: WorkflowStatus;
        let finalReviewStatusText: string;
        if (props.assessmentSubject.assessment_workflow_setup.due_date !== undefined && now > iso8601ToJsDate(props.assessmentSubject.assessment_workflow_setup.due_date)) {
            finalReviewStatus = 'overdue';
            finalReviewStatusText = 'Overdue';
        } else {
            finalReviewStatus = assessmentsStatus === 'complete' ? 'inProgress' : 'idle';
            finalReviewStatusText = assessmentsStatus === 'complete' ? 'In progress' : 'Waiting';
        }

        return { irqStatus, irqStatusText, questionnairesStatus, questionnairesStatusText, assessmentsStatus, assessmentsStatusText, finalReviewStatus, finalReviewStatusText };
    })();

    // Invitations only make sense while questionnaires are being answered, and only for an assessment subject that has its own assessments (rather than shared ones).
    const canInvite = props.assessmentSubject.assessment_workflow_data.state === AssessmentWorkflowState.PERFORMING_DUE_DILIGENCE && props.assessmentSubject.assessment_workflow_setup.common_assessment_parent === undefined;

    const riskWorkflowOverflowMenu = (() => {
        if (props.assessmentSubject.assessment_workflow_data.state === AssessmentWorkflowState.AWAITING_ASSESSMENT) {
            return <></>;
        }

        return (
            <OverflowMenu
                overflowItems={[
                    {
                        text: 'Clear workflow',
                        onClickAction: props.onSelectClearWorkflow,
                        icon: ICON_DELETE_REMOVE,
                    },
                    {
                        text: 'Restart workflow',
                        onClickAction: props.onSelectRestartWorkflow,
                        icon: ICON_RESTART,
                    },
                ]}
                accessibilityTitle="Due Diligence Workflow Menu"
                overflowDotsColor="white"
            />
        );
    })();

    const autoAdvanceStatusInfo: JSX.Element = (() => {
        if (autoAdvanceAvailability.isAvailable) {
            return (
                <>
                    <FontAwesomeIcon icon={ICON_AUTO_ADVANCE} />
                    <Text noStyles color="white" variant="Text2">
                        Auto-Advance enabled
                    </Text>
                    <FormFieldTooltip text={getDescriptionForAdvancedFeature(autoAdvanceFeature)} />
                </>
            );
        } else {
            return (
                <>
                    <FontAwesomeIcon icon={ICON_DISABLED_FEATURE} />
                    <Text noStyles color="white" variant="Text2">
                        Auto-Advance disabled
                    </Text>
                    <FormFieldTooltip text={<AdvancedFeatureUnavailableExplanation feature={autoAdvanceAvailability.feature} reason={autoAdvanceAvailability.reason} textColor="white" />} />
                </>
            );
        }
    })();

    return (
        <>
            <div className={styles.headerContainer}>
                <Text variant="Header2" color="white">
                    Due Diligence Workflow
                </Text>
                {riskWorkflowOverflowMenu}
            </div>
            <div className={styles.handsFreeModeContainer}>
                <div className={styles.handsFreeModeContainer}>{autoAdvanceStatusInfo}</div>
            </div>
            <div className={styles.workflowContainer}>
                <Station isBookend name="Inherent Risk Questionnaire" status={statuses.irqStatus} statusText={statuses.irqStatusText} />

                <Arrow />

                <div className={styles.middleGroup}>
                    <Station name="Questionnaires" status={statuses.questionnairesStatus} statusText={statuses.questionnairesStatusText} />
                    <Arrow />
                    <Station name="Assessments" status={statuses.assessmentsStatus} statusText={statuses.assessmentsStatusText} />
                </div>

                <Arrow />

                <Station isBookend name="Final Review" status={statuses.finalReviewStatus} statusText={statuses.finalReviewStatusText} />
            </div>
            <div className={styles.underStationsContainer}>
                <div className={styles.underBookendStationLinkContainer}>
                    <Link
                        size="sm"
                        to={generatePath(props.module === ProductModule.UCRM ? UCRM_INHERENT_RISK_QUESTIONNAIRE : TPRM_INHERENT_RISK_QUESTIONNAIRE, {
                            third_party_id: props.assessmentSubject.vendor_id,
                            service_id: props.assessmentSubject.id,
                        })}
                    >
                        View Inherent Risk Questionnaire
                    </Link>
                </div>
                <div className={styles.underStationsIconSpacer} />
                <div className={styles.underMiddleGroupContainer}>
                    {/** TPRM */}
                    {canInvite && props.onSelectInviteServiceContacts && (
                        <Button variant="linkText" size="sm" onClick={props.onSelectInviteServiceContacts}>
                            Invite third-party service contacts
                        </Button>
                    )}
                    {/** UCRM */}
                    {canInvite && props.onSelectInviteRespondents && (
                        <Button variant="linkText" size="sm" onClick={props.onSelectInviteRespondents}>
                            Invite respondents
                        </Button>
                    )}
                </div>
                <div className={styles.underStationsIconSpacer} />
                <div className={styles.underBookendStationLinkContainer}>
                    {props.assessmentSubject.assessment_workflow_data.state === AssessmentWorkflowState.PERFORMING_DUE_DILIGENCE && (
                        <Link
                            size="sm"
                            to={generatePath(props.module === ProductModule.UCRM ? UCRM_FINAL_REVIEW : TPRM_FINAL_REVIEW, {
                                third_party_id: props.assessmentSubject.vendor_id,
                                service_id: props.assessmentSubject.id,
                            })}
                        >
                            View Final Review
                        </Link>
                    )}
                </div>
            </div>
        </>
    );
};

interface StationProps {
    name: string;
    status: WorkflowStatus;
    statusText: string;

    isBookend?: boolean;
}

const Station = (props: StationProps): JSX.Element => {
    return (
        <div className={`${styles.stationContainer} ${props.isBookend ? styles.stationContainerBookend : styles.stationContainerMiddle}`}>
            <div className={`${styles.station} ${getBorderClass(props.status)}`}>
                <div className={styles.stationContent}>
                    <Text color="white" noStyles>
                        <b>{props.name}</b>
                    </Text>
                    <div className={`${styles.statusBadge} ${getStatusBadgeClass(props.status)}`}>
                        <Text color="white" noStyles>
                            {props.statusText}
                        </Text>
                    </div>
                </div>
            </div>
        </div>
    );
};

const Arrow = (): JSX.Element => {
    return (
        <div className={styles.arrow}>
            <FontAwesomeIcon icon={ICON_ARROW_RIGHT} />
        </div>
    );
};
